import get from "lodash/get";
import omit from "lodash/omit";
import * as React from "react";

import { setExchangeId, setScreenName } from "../../actions";
import { StoreState } from "../../reducers";
import { ExchangeRecord } from "../../reducers/exchange/exchanges";
import {
  getExchanges,
  getIsLoadingExchanges,
} from "../../reducers/exchange/methods";
import {
  getDisplaySize,
  getLedgerHead,
  getRewardRequestStatus,
} from "../../reducers/methods";
import { getRewards, getShopifyVariants } from "../../reducers/reward/methods";
import { ShopifyVariantRecord } from "../../reducers/reward/shopifyVariants";
import { store } from "../../store";
import { formatUserFacingNumber } from "../../utils/number";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { WidgetConfig } from "../Widget/Widget";
import "./RedemptionOptions.css";

const defaultMinWidth = "120px";

const exampleOptions: ExchangeRecord[] = [
  {
    id: "00000000-0000-0000-0000-000000000000",
    createdOn: "2021-12-16T01:45:37.362Z",
    exchangeType: "standard",
    ledgerAmount: 500,
    ledgerCurrency: " Points",
    name: "Free Travel ",
    status: "active",
    reward: {
      rewardType: "discount",
      delivery: "codeBank",
    },
    imageSrc:
      "https://media.fbot-sandbox.me/0bd0ac6c-ee1f-42de-a6b5-fc40d9b74a7d/media/travelsized.png",
    displayImage: true,
    position: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000001",
    createdOn: "2021-12-16T02:45:37.362Z",
    exchangeType: "standard",
    ledgerAmount: 750,
    ledgerCurrency: " Points",
    name: "Shampoo",
    status: "active",
    reward: {
      rewardType: "discount",
      delivery: "codeBank",
    },
    imageSrc:
      "https://media.fbot-sandbox.me/0bd0ac6c-ee1f-42de-a6b5-fc40d9b74a7d/media/fullsizedshampoo.png",
    displayImage: true,
    position: 1,
  },
  {
    id: "00000000-0000-0000-0000-000000000002",
    createdOn: "2021-12-16T03:45:37.362Z",
    exchangeType: "standard",
    ledgerAmount: 1000,
    ledgerCurrency: " Points",
    name: "Your Own Kit",
    status: "active",
    reward: {
      rewardType: "discount",
      delivery: "codeBank",
    },
    imageSrc:
      "https://media.fbot-sandbox.me/0bd0ac6c-ee1f-42de-a6b5-fc40d9b74a7d/media/makeyourownkit.png",
    displayImage: true,
    position: 2,
  },
];

const sortByAmount = (a: ExchangeRecord, b: ExchangeRecord) =>
  get(a, "amount", 0) > get(b, "amount", 0) ? 1 : -1;
const sortByPosition = (a: ExchangeRecord, b: ExchangeRecord) =>
  get(a, "position", 0) - get(b, "position", 0);

/** Returns the config key for the screen whose label contains "confirmation", or undefined. */
function getConfirmationScreenKey(config: WidgetConfig): string | undefined {
  for (const [key, valueObj] of Object.entries(config)) {
    const screenName = get(valueObj, "label", "").toLowerCase();
    if (screenName.includes("confirmation")) {
      return key;
    }
  }

  return undefined;
}

export interface RedemptionOptionsProps extends ComponentBaseProps {
  action: string;
  classNames: string;
  config: WidgetConfig;
  configName: string;
  isLoading: ReturnType<typeof getIsLoadingExchanges>;
  name: string;
  redemptionOptions: ReturnType<typeof getExchanges>;
  shopifyVariants: ReturnType<typeof getShopifyVariants>;
  rewards: ReturnType<typeof getRewards>;
  requestStatus: ReturnType<typeof getRewardRequestStatus>;
  setExchangeId: typeof setExchangeId;
  setScreenName: typeof setScreenName;
  align?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  amountStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  arrowStyle?: React.CSSProperties;
  buttonText?: string;
  cardStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
  ledgerHead: ReturnType<typeof getLedgerHead>;
  nameStyle?: React.CSSProperties;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noOptionsText?: string;
  noOptionsStyle?: React.CSSProperties;
  /** When provided, used for redemption so original (non-normalized) email is preserved; optional when only normalized email is available. */
  originalEmail?: string;
  style?: React.CSSProperties;
  testMode?: string;
  rowStyle?: React.CSSProperties;
  wrapCards?: boolean;
  currencyLabel?: string;
  /** Optional legacy callback; redemption is triggered on the confirmation screen. */
  redeem?: (params: { exchangeId: string; originalEmail?: string }) => void;
  isLoadingRedeemExchanges?: string;
}

interface ButtonProps {
  buttonText?: string;
  config: WidgetConfig;
  disabled: boolean;
  exchangeId: string;
  setExchangeId: typeof setExchangeId;
  setScreenName: typeof setScreenName;
  style: React.CSSProperties;
  isLoadingRedeemExchanges?: string;
  originalEmail?: string;
}

const ScrollButton = ({
  direction,
  onClick,
  disabled = false,
  styles,
}: {
  direction: "prev" | "next";
  onClick: React.MouseEventHandler;
  disabled: boolean;
  styles?: React.CSSProperties;
}) => (
  <div
    className={`RedemptionOptions-scroll-button ${direction} ${
      disabled ? "disabled" : ""
    }`}
    onClick={onClick}
    style={{ ...styles }}
  >
    <div className={`RedemptionOptions-scroll-button-inner ${direction}`} />
  </div>
);

export class RedeemButton extends React.Component<ButtonProps> {
  handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (this.props.disabled) {
      return;
    }
    const { exchangeId, setExchangeId, isLoadingRedeemExchanges } = this.props;
    // Guard first so we never call setExchangeId while another redeem is in progress (global loading state is intentional: one redeem at a time).
    if (isLoadingRedeemExchanges === "loading") {
      return;
    }
    setExchangeId(exchangeId);
    // Both Loyalty and Reward Store follow confirmation-first flow:
    // tile click selects the exchange and navigates; redeem API is called from
    // the confirmation step (RedeemExchange).
    this.setScreen(this.props.config);
  };

  setScreen = (config: WidgetConfig) => {
    const key = getConfirmationScreenKey(config);
    if (key) {
      this.props.setScreenName(key);
    } else {
      console.warn(`Widget must be configured with "confirmation" screen.`);
    }
  };

  render() {
    const isDisabled =
      this.props.disabled || this.props.isLoadingRedeemExchanges === "loading";
    const style = {
      ...this.props.style,
      ...(isDisabled ? {} : { cursor: "pointer" }),
    };

    return (
      <div
        className="RedemptionOptions-button-cell"
        style={style}
        onClick={this.handleClick}
      >
        <div>
          {this.props.isLoadingRedeemExchanges === "loading"
            ? "..."
            : this.props.buttonText || "REDEEM"}
        </div>
      </div>
    );
  }
}

export class RedemptionOptions extends ComponentBase<RedemptionOptionsProps> {
  swiperContainerRef: React.RefObject<HTMLDivElement>;

  constructor(props: RedemptionOptionsProps) {
    super(props);
    this.swiperContainerRef = React.createRef();
  }

  componentDidUpdate(prevProps: RedemptionOptionsProps): void {
    const swiperContainer = this.getSwiperContainer();

    if (swiperContainer && !this.state.scrollState) {
      const scrollState = this.calculateScrollState(swiperContainer);
      this.setState(() => ({
        scrollState: { ...scrollState },
      }));

      swiperContainer.addEventListener("scroll", () => {
        const scrollState = this.calculateScrollState(swiperContainer);

        this.setState(() => ({
          scrollState: { ...scrollState },
        }));
      });
    }
  }

  getSwiperContainer = () =>
    this.swiperContainerRef.current
      ? this.swiperContainerRef.current.parentElement
      : null;

  // calculateScrollState() calculates whether they are able to scroll
  // left or right based on the current scroll position
  calculateScrollState = (swiperContainer: HTMLElement) => {
    const scrollPrev = swiperContainer.scrollLeft > 0;
    const scrollNext =
      swiperContainer.scrollLeft <
      swiperContainer.scrollWidth - swiperContainer.offsetWidth;

    return {
      scrollNext,
      scrollPrev,
    };
  };

  renderNoOptions = () => {
    return (
      <div style={this.props.noOptionsStyle}>
        {this.props.noOptionsText || ""}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || "No Redemption Options Configured"}
      </div>
    );
  };

  getContentTestMode = (
    redemptionOptions: ExchangeRecord[],
    testMode?: string
  ) => {
    switch (testMode) {
      case "notAuthenticated":
        return this.renderNotAuthenticated();
      case "noRedemptionOptions":
        return this.renderNoOptions();
      case "RedemptionOptions":
        return this.renderRedemptionOptions(redemptionOptions);
      default:
        return this.renderRedemptionOptions(redemptionOptions);
    }
  };

  getContent = (
    redemptionOptions: ExchangeRecord[],
    isCustomerAuthenticated: boolean
  ) => {
    if (isCustomerAuthenticated) {
      const validOptions = redemptionOptions.filter((option) =>
        this.validOption(option)
      );
      if (validOptions.length === 0) {
        return this.renderNoOptions();
      }

      return this.renderRedemptionOptions(validOptions);
    } else {
      return this.renderNotAuthenticated();
    }
  };

  validOption = (exchange: ExchangeRecord) => {
    if (!exchange) {
      return false;
    }
    if (exchange.status !== "active" || exchange.default) {
      return false;
    }
    if (exchange.exchangeType !== "standard") {
      return false;
    }
    if (exchange.reward && exchange.reward.rewardType === "shopifyFreeGift") {
      return this.isShopifyFreeGiftInStock(exchange);
    }

    return true;
  };

  isShopifyFreeGiftInStock = (exchange: ExchangeRecord) => {
    const { shopifyVariants, requestStatus } = this.props;

    if (requestStatus.getShopifyVariants === "FAILURE") {
      return false;
    }

    const freeGiftConfig =
      exchange.reward && exchange.reward.shopifyFreeGiftConfig;
    if (!freeGiftConfig) {
      return false;
    }

    const productVariantId =
      freeGiftConfig.productVariant && freeGiftConfig.productVariant.id;
    const fallbackVariantId =
      freeGiftConfig.fallbackProductVariant &&
      freeGiftConfig.fallbackProductVariant.id;

    if (!productVariantId) {
      return false;
    }

    const variants: ShopifyVariantRecord[] =
      (shopifyVariants && shopifyVariants.records) || [];

    const matchingVariants = variants.filter(
      (v) => v.id === productVariantId || v.id === fallbackVariantId
    );

    if (matchingVariants.length === 0) {
      return false;
    }

    const allOutOfStock = matchingVariants.every((v) => {
      const quantity = v.inventoryQuantity || 0;

      // If inventoryPolicy is CONTINUE, allow redemption even if quantity <= 0
      if (v.inventoryPolicy === "CONTINUE") {
        return false;
      }

      return quantity <= 0;
    });

    return !allOutOfStock;
  };

  hasShopifyRedeemedGift(
    rewards: any[],
    mainProductVariantId: string | null,
    fallbackProductVariantId: string | null
  ) {
    return (
      rewards &&
      rewards.some((reward: any) => {
        if (!reward || !reward.reward || !reward.definition) {
          return false;
        }

        return (
          reward.reward.rewardType === "shopifyFreeGift" &&
          reward.status === "ready" &&
          !reward.redemptionInfo &&
          (reward.definition.value === mainProductVariantId ||
            reward.definition.value === fallbackProductVariantId)
        );
      })
    );
  }

  shouldDisable = (exchange: ExchangeRecord) => {
    const { ledgerHead, rewards } = this.props;

    let mainProductVariantId: any;
    let fallbackProductVariantId: any;

    if (exchange && exchange.reward && exchange.reward.shopifyFreeGiftConfig) {
      const config = exchange.reward.shopifyFreeGiftConfig;

      if (config.productVariant) {
        mainProductVariantId = config.productVariant.id;
      }

      if (config.fallbackProductVariant) {
        fallbackProductVariantId = config.fallbackProductVariant.id;
      }
    }

    if (ledgerHead && ledgerHead.currency === exchange.ledgerCurrency) {
      if (Number(ledgerHead.balance) < exchange.ledgerAmount) {
        return true;
      }
    }

    return this.hasShopifyRedeemedGift(
      rewards,
      mainProductVariantId,
      fallbackProductVariantId
    );
  };

  splitOptionsIntoRows = (
    redemptionOptions: ExchangeRecord[],
    numberOfRows: number
  ) => {
    // Calculate the number of items per row (as evenly as possible)
    const itemsPerRow = Math.ceil(redemptionOptions.length / numberOfRows);

    // Split redemption options into rows based on the specified number of rows
    const rows = [];
    for (let i = 0; i < redemptionOptions.length; i += itemsPerRow) {
      rows.push(redemptionOptions.slice(i, i + itemsPerRow));
    }

    return rows;
  };

  getCardStyle = (
    record: ExchangeRecord,
    cardStyle: React.CSSProperties | undefined
  ): React.CSSProperties => {
    const cardWidth = cardStyle ? cardStyle.width : defaultMinWidth;
    // When wrapCards: use flex "0 0 auto" (no grow/shrink) + width so each card keeps a fixed
    // width and wraps to the next line predictably. When not wrapping: use flex 1 (grow to fill
    // space) + minWidth so cards share space evenly while respecting a minimum size.
    const flexProps: React.CSSProperties = this.props.wrapCards
      ? { flex: "0 0 auto", width: cardWidth }
      : { flex: 1, minWidth: cardWidth };

    return {
      ...cardStyle,
      ...flexProps,
      opacity: this.shouldDisable(record) ? 0.5 : 1,
    };
  };

  renderRedemptionOptions = (
    redemptionOptions: ExchangeRecord[],
    testMode?: string
  ) => {
    const {
      amountStyle,
      cardStyle,
      buttonStyle,
      buttonText,
      cellStyle,
      config,
      classNames,
      imageStyle,
      nameStyle,
      rowStyle,
    } = this.props;

    const rightStyle = { width: "50%", float: "right" } as React.CSSProperties;
    // Padding is accessible within the builder, margins are not.
    // Split padding out to the parent container so that it functions like a margin.
    const imageStylePadding = {
      paddingTop: get(imageStyle, "paddingTop", "0px"),
      paddingLeft: get(imageStyle, "paddingLeft", "0px"),
      paddingBottom: get(imageStyle, "paddingBottom", "0px"),
      paddingRight: get(imageStyle, "paddingRight", "0px"),
    };
    const imageStyleWithoutPadding = {
      ...omit(imageStyle, [
        "paddingTop",
        "paddingLeft",
        "paddingBottom",
        "paddingRight",
      ]),
    };

    // signifies a merchant has added the updated default Redemption Option && builder version
    const hasStyling = get(imageStyle, "width");
    const hideImageTestMode = get(imageStyle, "hideImageTestMode", false);
    const hasImageSettings = hasStyling && !hideImageTestMode;

    const useLegacySort = !redemptionOptions.every(
      (option) => typeof option.position === "number"
    );

    const sortedOptions = redemptionOptions.sort(
      useLegacySort ? sortByAmount : sortByPosition
    );

    // Get current display size from Redux store
    const state = store.getState() as StoreState;
    const displaySize = getDisplaySize(state);
    // Check if multi-row display should only be shown on mobile
    const displayRowsMobileOnly = get(rowStyle, "mobileOnly", false);

    // Get number of rows to display, defaulting to 1 if not specified
    let numRows = get(rowStyle, "numRows", 1);
    // If not on mobile and mobile-only is enabled, force single row
    if (displaySize !== "mobile" && displayRowsMobileOnly) {
      numRows = 1;
    }
    // When wrapCards is true, put all options in a single row so flex wrap controls
    // how many cards per row; when false, split by numRows for fixed rows (e.g. carousel).
    const rowGroups = this.props.wrapCards
      ? [sortedOptions]
      : this.splitOptionsIntoRows(sortedOptions, numRows);

    return (
      <>
        <div
          ref={this.swiperContainerRef}
          className={`RedemptionOptions-container ${classNames || ""}`}
          aria-label="Redemption Options"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            ...(this.props.wrapCards ? { width: "100%" } : {}),
          }}
        >
          {rowGroups.map((rowOptions, rowIndex) => (
            <div
              className="RedemptionOptions-row"
              key={`row-${rowIndex}`}
              style={{
                display: "flex",
                flexWrap: this.props.wrapCards ? "wrap" : undefined,
                gap: "10px",
                alignSelf: "start",
              }}
            >
              {rowOptions.map((record) => (
                <div
                  key={`${record.id}`}
                  style={this.getCardStyle(record, cardStyle)}
                >
                  <div>
                    {record.imageSrc &&
                      record.displayImage &&
                      hasImageSettings && (
                        <div
                          style={{
                            width: "50%",
                            float: "left",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ ...imageStylePadding }}>
                            <img
                              src={record.imageSrc}
                              alt={record.name}
                              style={{ ...imageStyleWithoutPadding }}
                            />
                          </div>
                        </div>
                      )}
                    <div
                      style={
                        record.displayImage && hasImageSettings
                          ? rightStyle
                          : {}
                      }
                    >
                      <div
                        className="RedemptionOptions-cell"
                        style={{
                          margin: 0,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          ...cellStyle,
                          ...nameStyle,
                        }}
                      >
                        {record.name}
                      </div>
                      <div
                        className="RedemptionOptions-cell"
                        style={{
                          margin: 0,
                          padding: 0,
                          ...cellStyle,
                          ...amountStyle,
                        }}
                      >
                        {formatUserFacingNumber(record.ledgerAmount)}{" "}
                        {this.props.currencyLabel || record.ledgerCurrency}
                      </div>
                      <div
                        style={{
                          margin: "0 auto",
                          padding: 0,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <RedeemButton
                          buttonText={buttonText}
                          config={config}
                          disabled={this.shouldDisable(record)}
                          exchangeId={record.id}
                          setExchangeId={this.props.setExchangeId}
                          setScreenName={this.props.setScreenName}
                          isLoadingRedeemExchanges={
                            this.props.isLoadingRedeemExchanges
                          }
                          originalEmail={this.props.originalEmail}
                          style={{
                            margin: 0,
                            ...cellStyle,
                            ...buttonStyle,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </>
    );
  };

  render() {
    const { data, isLoading, testMode, isEditing, style } = this.props;
    const { scrollState = { scrollPrev: false, scrollNext: false } } =
      this.state;

    const redemptionOptions = isEditMode()
      ? exampleOptions
      : this.props.redemptionOptions || [];

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);

    const swiperContainer = this.getSwiperContainer();

    const onScrollClick = (direction: "prev" | "next") => () => {
      if (swiperContainer) {
        const containerPadding = 48;
        const validOptions = redemptionOptions.filter((option) =>
          this.validOption(option)
        );

        const scrollAmount =
          (swiperContainer.scrollWidth - containerPadding) /
          validOptions.length;
        const scrollPosition =
          direction === "prev" ? -scrollAmount : scrollAmount;

        swiperContainer.scrollBy({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    };

    const wrapCardsContainerStyle: React.CSSProperties = this.props.wrapCards
      ? { overflow: "visible" }
      : {};
    const wrapCardsContentStyle: React.CSSProperties = this.props.wrapCards
      ? { overflowX: "visible", width: "100%" }
      : {};

    if (isEditing) {
      return (
        <div
          style={{ ...style, ...wrapCardsContainerStyle }}
          id={this.props.configName}
          onDragStart={this.onDragStart}
          onMouseDown={this.onMouseDown}
          ref={(component) => (this.component = component)}
          className={`RedemptionOptions-div-container`}
        >
          <div
            className="RedemptionOptions-content"
            style={wrapCardsContentStyle}
          >
            {this.getContentTestMode(redemptionOptions, testMode)}
          </div>
          {!this.props.wrapCards && (
            <>
              <ScrollButton
                direction="prev"
                onClick={onScrollClick("prev")}
                disabled={!scrollState.scrollPrev}
                styles={{ ...this.props.arrowStyle }}
              />
              <ScrollButton
                direction="next"
                onClick={onScrollClick("next")}
                disabled={!scrollState.scrollNext}
                styles={{ ...this.props.arrowStyle }}
              />
            </>
          )}
        </div>
      );
    }

    if (isLoading) {
      return "...";
    }

    return (
      <div
        style={{ ...style, ...wrapCardsContainerStyle }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        ref={(component) => (this.component = component)}
        className={`RedemptionOptions-div-container`}
      >
        <div
          className="RedemptionOptions-content"
          style={wrapCardsContentStyle}
        >
          {this.getContent(redemptionOptions, isCustomerAuthenticated)}
        </div>
        {!this.props.wrapCards && (
          <>
            <ScrollButton
              direction="prev"
              onClick={onScrollClick("prev")}
              disabled={!scrollState.scrollPrev}
              styles={{ ...this.props.arrowStyle }}
            />
            <ScrollButton
              direction="next"
              onClick={onScrollClick("next")}
              disabled={!scrollState.scrollNext}
              styles={{ ...this.props.arrowStyle }}
            />
          </>
        )}
      </div>
    );
  }
}
